/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { Tooltip, TooltipVariant } from '../Tooltip';
import { IListItem, List } from '../List';
import { Focusable } from '../Focusable';

import {
    GROUP_INPUT,
    GROUP,
    GROUP_LABEL,
    GROUP_CHECKBOX,
    GROUP_INPUT_ELEMENT,
    GROUP_TEXT_DEFAULT,
    GROUP_PLACEHOLDER,
    GROUP_BLINK,
    GROUP_FOCUS,
    GROUP_ERROR,
    CONTROL_FOCUSED,
    CONTROL_SELECTED,
    GROUP_VALUE,
    CONTROL_ONBLUR
} from '../utils/classes';
import { autobind } from '../../../core/lib';
import cls from '../utils/classnames';
import { getValidations, getTitle, TranslationID } from './utils';
import './_styles.scss';

export const TEXT = 'text';
export const EMAIL = 'email';
export const PASSWORD = 'password';
export const PASSWORD_SHOWN = 'password_shown';
export const CHECKBOX = 'checkbox';
export const MOBILE = 'mobile';
export const VALIDATION_CODE = 'validation_code';
export const ALPHANUMERIC = 'alphanumeric';
export const PIN = 'pin';
export const NUMERIC = 'numeric';

export interface IProps {
    focusWhenMount?: boolean;
    selectedInput?: string;
    id?: string;
    name: string;
    label?: string;
    type: string;
    value: string;
    placeholder?: string;
    errorFromBackend?: string;
    onClick?: (args: unknown) => void;
    onInputIsValid?: (args: unknown) => void;
    focused?: boolean;
    onFocus(): void;
    classNamePass?: string;
    maxLength?: number;
    validInput?: boolean;
    showTooltipSuccess?: boolean;
    translations?: Record<TranslationID, string>
    checked?: boolean
    submit?: boolean;
    onChange?: (args: unknown) => void;
    children?: React.ReactChild;
    error?: boolean;
}

interface IState {
    focused: boolean;
    terms: boolean;
    selected: boolean;
}

export class Input extends React.Component<IProps, IState> {
    private _isValid: boolean;
    private isSubmit: boolean;
    onFocus: () => void;
    constructor(props: IProps) {
        super(props);
        this.state = {
            focused: false,
            terms: false,
            selected: false,
        };
        this.getInputElement = this.getInputElement.bind(this);
        this.getPasswordShownInput = this.getPasswordShownInput.bind(this);
        this.getTooltipElement = this.getTooltipElement.bind(this);
        this.setFocused = this.setFocused.bind(this);
        this.getClassType = this.getClassType.bind(this);
    }

    componentDidMount() {
        if(this.props.focusWhenMount){
            this.setState({ selected: true });

            const { onClick } = this.props;
            if (typeof onClick === 'function') {
                onClick('');
            }
        }
    }

    componentDidUpdate() {
        if(this.props.selectedInput && this.props.selectedInput !== ''){
            if( this.props.selectedInput === this.props.name && !this.state.selected){
                this.setState({ selected: true });
            }else if( this.props.selectedInput !== this.props.name && this.state.selected){
                this.setState({ selected: false });
            }
        }
    }

    @autobind
    private getTooltipElement(type: string, value: string, translations?: Record<TranslationID, string> ): JSX.Element {
        const { classNamePass, error } = this.props;
        const { title, variant } = getTitle(type, translations);
        const checkList = getValidations(type, value, translations, error);
        this.setInputIsValid(checkList);
        return (
            <Tooltip
                variant={variant as TooltipVariant}
                title={title}
                passWordClass={classNamePass}
            >
                <List isPassword =  {true} variant={variant} items={checkList} />
            </Tooltip>
        );
    }

    @autobind
    public setInputIsValid(validations: IListItem[]): void {
        const errValidations: IListItem[] = validations.filter((item: IListItem) => {
            return item.variant === 'error' || item.variant === 'default';
        });
        const isValid = errValidations.length === 0;
        if (this._isValid !== isValid) {
            this._isValid = isValid;
        }
    }

    private getTooltipError(message: string): JSX.Element {
        const { classNamePass } = this.props;
        const variant = 'error';
        const checkList: IListItem[] = [];
        checkList.push({
            text: message,
            variant: variant
        });
        return (
            <Tooltip variant={variant as TooltipVariant} passWordClass={classNamePass}>
                <List variant={variant} items={checkList} />
            </Tooltip>
        );
    }

    private getLabelPassword(value: string): string {
      let { maxLength } = this.props;
      maxLength = maxLength ? maxLength : 30;
      value = value.slice(value.length >= maxLength ? value.length - maxLength : 0, value.length);
      return value.split('').reduce((label) => `${label}*`, '');
    }

    private getInputElement(
        type: string,
        value: string,
        placeholder?: string,
        blinkingPipe?: JSX.Element
    ): JSX.Element | null {
        let inputElement: JSX.Element | null = null;
        switch (type) {
            case PASSWORD_SHOWN:
                inputElement = this.getPasswordShownInput(value, placeholder, blinkingPipe);
                break;
            case PASSWORD:
                inputElement = this.getPasswordInput(value, placeholder, blinkingPipe);
                break;
            case VALIDATION_CODE:
                inputElement = this.getAlphanumericInput(value, placeholder, blinkingPipe);
                break;
            case NUMERIC:
            case PIN:
            case MOBILE:
                inputElement = this.getNumberInput(value, placeholder, blinkingPipe);
                break;
            case EMAIL:
            case TEXT:
            default:
                inputElement = this.getTextInput(value, placeholder, blinkingPipe);
        }
        return inputElement;
    }

    private getNumberInput(value: string, placeholder = '', blinkingPipe?: JSX.Element): JSX.Element {
        const { name } = this.props;
        return value.length ? (
            <div className={GROUP_TEXT_DEFAULT}>
                {name === 'cvv' ? this.getLabelPassword(value) : this.convertStringToHtmlNumber(value)}
                {blinkingPipe}
            </div>
        ) : (
            <div className={GROUP_PLACEHOLDER}>
                {blinkingPipe}
                {placeholder}
            </div>
        );
    }

    private getAlphanumericInput(value: string, placeholder = '', blinkingPipe?: JSX.Element): JSX.Element {
        const { name } = this.props;
        return value.length ? (
            <div className={GROUP_TEXT_DEFAULT}>
                {this.convertStringToAlphanumeric(value)}
                {blinkingPipe}
            </div>
        ) : (
            <div className={GROUP_PLACEHOLDER}>
                {blinkingPipe}
                {placeholder}
            </div>
        );
    }

    private getTextInput(value: string, placeholder = '', blinkingPipe?: JSX.Element): JSX.Element {
        return value.length ? (
            <div className={GROUP_TEXT_DEFAULT}>
                {this.convertStringToHtml(value, false)}
                {blinkingPipe}
            </div>
        ) : (
            <div className={GROUP_PLACEHOLDER}>
                {blinkingPipe}
                {placeholder}
            </div>
        );
    }

    private removeStyleInput(){
        let inputMail = document.getElementById('emailInput');
        inputMail && inputMail.style && (inputMail.style.border = '');
    }

    private getCheckboxInput(value: string, translations?: Record<TranslationID,string>): JSX.Element {
        const { type, label, name, checked, onChange} = this.props;
        const { terms } = this.state;

        return (
          <div className={GROUP_CHECKBOX} onFocus = {this.removeStyleInput}>
            <a className={cls('aaf_button-default','focusable', 'checkbox-aaf-element')} data-checked={checked} id={name} name={name} href="javascript:void(0)"
               tabIndex={0} onClick={(event) => {
              event.preventDefault();
              event.target.dataset.checked = event.target.dataset.checked === 'false' ? 'true' : 'false';
              if (onChange) {
                onChange(event.target.dataset.checked === 'true');
              }
            }}></a>
            <label className={`${GROUP_LABEL} label-${type}`}>{label}</label>
            {!checked &&  this.props.submit && this.getTooltipElement(type, String(terms), translations)}
          </div>
        );
    }

    private getPasswordInput(
        value: string,
        placeholder = '',
        blinkingPipe?: JSX.Element
    ): JSX.Element {
        return value.length ? (
            <div className={GROUP_TEXT_DEFAULT}>
                {this.getLabelPassword(value)}
                {blinkingPipe}
            </div>
        ) : (
            <div className={GROUP_PLACEHOLDER}>
                {blinkingPipe}
                {placeholder}
            </div>
        );
    }

    private getPasswordShownInput(
        value: string,
        placeholder = '',
        blinkingPipe?: JSX.Element
    ): JSX.Element {
        const classNameGroup = value.length ? GROUP_TEXT_DEFAULT : GROUP_PLACEHOLDER;
        return (
            <div className={classNameGroup}>
                {GROUP_PLACEHOLDER === classNameGroup && blinkingPipe}
                {value.length > 0 ? this.convertStringToHtml(value, true) : placeholder}
                {GROUP_PLACEHOLDER !== classNameGroup && blinkingPipe}
            </div>
        );
    }

    private setFocused(value: boolean): void {
        this.setState({ focused: value});
    }

    private getClassType(type: string): string {
        let classType = type;
        const classTypesSimilarToText = [TEXT, EMAIL, PASSWORD_SHOWN];
        if (classTypesSimilarToText.includes(type)) {
            classType = TEXT;
        }
        return classType;
    }

    private convertStringToHtml(value: string, isShort: boolean = false): string {
        let { maxLength } = this.props;
        maxLength = maxLength ? maxLength : isShort ? 29 : 47;
        value = value.slice(value.length >= maxLength ? value.length - maxLength : 0, value.length);
        return value.replace(/ /g, '\xa0');
    }

    private convertStringToHtmlNumber(value: string): string {
        let { maxLength } = this.props;
        maxLength = maxLength ? maxLength :47;
        value = value.slice(value.length >= maxLength ? value.length - maxLength : 0, value.length);
        return value.replace(/[^0-9]/g, "");
    }

    private convertStringToAlphanumeric(value: string): string {
        let { maxLength } = this.props;
        maxLength = maxLength ? maxLength :47;
        value = value.slice(value.length >= maxLength ? value.length - maxLength : 0, value.length);
        return value.replace(/[^a-zA-Z0-9]/g, "");
    }
    @autobind
    private selectElement(event: any): void {
        //TODO - eliminar esto despues de probar
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;

        this.setState({
            terms: value,
        });
        if (!event.keyCode) {
            // What to do?
            const { onClick } = this.props;
            if (typeof onClick === 'function') {
                onClick(event);
            }
        }
    }

    @autobind
    private onBlur (event: any): void  {
        this.setFocused(false);
    }

    public render(): JSX.Element {
        let {
            id,
            value,
            type,
            placeholder = '',
            errorFromBackend = '',
            onFocus,
            validInput,
            showTooltipSuccess = false,
            translations,
            submit = false,
            children,
            error
        } = this.props;

        this.isSubmit = submit  ;
        const { focused, selected } = this.state;
        let toolTip = null;
        this.onFocus = () => {
            onFocus();
            this.setFocused(true);
        };
        const blinkingPipe = selected && focused ? <div className={cls(GROUP_BLINK)}>|</div> : undefined;

        if (!focused && !validInput) {
            toolTip =
                errorFromBackend !== ''
                    ? this.getTooltipError(errorFromBackend)
                    : showTooltipSuccess ? this.getTooltipElement(type, value, translations) : '';
        } else{
            this._isValid = true;
        }

        if ((focused) && (this.props.name == 'password') ) {
            errorFromBackend = '';

            toolTip =
                errorFromBackend !== ''
                    ? this.getTooltipError(errorFromBackend)
                    : showTooltipSuccess ? this.getTooltipElement(type, value, translations) : '';
        } else{
            this._isValid = true;
        }

        return (
            <div onFocus={this.onFocus} onBlur={this.onBlur} tabIndex={-1} className={cls(GROUP_INPUT, GROUP)}>
                <div className={GROUP_INPUT_ELEMENT}>
                    {type === 'checkbox' ? (
                        this.getCheckboxInput(value, translations)
                    ) : (
                        <Focusable
                        id={id}
                        onClick={this.selectElement}
                        className={`focusable ${cls(GROUP_FOCUS, `${GROUP_FOCUS}-${type}`, {
                            [`${CONTROL_FOCUSED}`]: Boolean(focused)
                        }, {
                            [`${CONTROL_ONBLUR}`]: Boolean(!focused)
                        }, {
                            [`${GROUP_ERROR}`]: Boolean(!focused && !validInput && (errorFromBackend !== '' || error))
                        })}`}
                        >
                            <div
                                className={cls(
                                    GROUP_VALUE,
                                    `${GROUP_VALUE}-${this.getClassType(type)}`
                                )}
                            >
                                {this.getInputElement(type, value, placeholder, blinkingPipe)}
                                {toolTip}
                                {children}
                            </div>
                        </Focusable>
                    )}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/packages/components/lib/Input/index.tsx